import React from 'react';
import clsx from 'clsx';

export interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: 'solid' | 'outline' | 'ghost';
  color?: 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'neutral';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  block?: boolean;
  icon?: React.ReactNode;
  htmlType?: 'button' | 'submit' | 'reset';
  className?: string;
  children?: React.ReactNode;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'solid',
      color = 'primary',
      size = 'md',
      loading = false,
      disabled = false,
      block = false,
      icon,
      htmlType = 'button',
      className,
      children,
      onClick,
      ...rest
    },
    ref
  ) => {
    const buttonClasses = clsx(
      'btn',
      'transition-all',
      'duration-200',
      'ease-in-out',

      {
        'btn-xs': size === 'xs',
        'btn-sm': size === 'sm',
        'btn-md': size === 'md',
        'btn-lg': size === 'lg'
      },

      {
        'btn-block': block
      },

      variant === 'solid' && {
        'btn-primary': color === 'primary',
        'btn-secondary': color === 'secondary',
        'btn-success': color === 'success',
        'btn-warning': color === 'warning',
        'btn-error': color === 'error',
        'btn-info': color === 'info',
        'btn-neutral': color === 'neutral'
      },

      variant === 'outline' && {
        'btn-outline': true,
        'btn-primary': color === 'primary',
        'btn-secondary': color === 'secondary',
        'btn-success': color === 'success',
        'btn-warning': color === 'warning',
        'btn-error': color === 'error',
        'btn-info': color === 'info',
        'btn-neutral': color === 'neutral'
      },

      variant === 'ghost' && {
        'btn-ghost': true
      },

      className
    );

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (loading || disabled) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    const spinnerSize = size === 'xs' || size === 'sm' ? 'loading-xs' : 'loading-sm';

    return (
      <button
        ref={ref}
        type={htmlType}
        className={buttonClasses}
        disabled={disabled || loading}
        onClick={handleClick}
        {...rest}
      >
        {loading && (
          <span className={`loading loading-spinner ${spinnerSize}`} />
        )}
        {!loading && icon && (
          <span className={clsx('flex items-center', children && 'mr-2')}>
            {icon}
          </span>
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
